import React, { useState } from 'react'
import { Box, Paper, Typography, CircularProgress, Button } from '@mui/material'
import { useAtom } from 'infrastructure/state/jotai.wrapper'
import { googleUserAtom, GoogleLoginButton } from 'infrastructure/googleDrive'
import { Instructions } from './components/Instructions'
import { SpreadsheetInput, ErrorDisplay } from 'shared/ui'
import { UrlMappingTable } from './components/UrlMappingTable'
import { extractSpreadsheetId } from 'shared/utils'
import { validateSpreadsheetData } from './utils/validation'
import { listTabs, getTabValuesByTitle } from 'infrastructure/googleDrive'
import { UrlMapping, ValidationError } from './types'
import { startUrlReplacement } from './api'
import { FlowIdView } from 'components/contentService/JobView'
import { UrlReplacementJob } from 'components/contentService/types'

type ViewState = 'instructions' | 'input' | 'loading' | 'success' | 'error'

interface UrlSwapProps {
  treeId: string
  versionId: string
}

export const UrlSwap: React.FC<UrlSwapProps> = ({ treeId, versionId }) => {
  const [googleUser] = useAtom(googleUserAtom)
  const [viewState, setViewState] = useState<ViewState>('instructions')
  const [error, setError] = useState<string | null>(null)
  const [spreadsheetData, setSpreadsheetData] = useState<UrlMapping[]>([])
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>(
    [],
  )
  const [flowId, setFlowId] = useState<string>()
  const [replacementLoading, setReplacementLoading] = useState(false)
  const [replacementError, setReplacementError] = useState<string | null>(null)

  const isAuthenticated = !!googleUser.credential?.accessToken

  const handleLoadSpreadsheet = async (url: string) => {
    const spreadsheetId = extractSpreadsheetId(url)
    if (!spreadsheetId) {
      setError('Invalid spreadsheet URL or ID')
      setViewState('error')
      return
    }

    if (!googleUser.credential?.accessToken) {
      setError('Not authenticated. Please log in with Google.')
      setViewState('error')
      return
    }

    setViewState('loading')
    setError(null)

    try {
      // First, list tabs to get the first sheet
      const tabs = await listTabs(
        spreadsheetId,
        googleUser.credential.accessToken,
      )

      if (!tabs || tabs.length === 0) {
        throw new Error('No sheets found in the spreadsheet')
      }

      // Get the first tab's data
      const firstTab = tabs[0]
      const response = await getTabValuesByTitle({
        spreadsheetId,
        title: firstTab.title,
        accessToken: googleUser.credential.accessToken,
      })

      if (!response.values || response.values.length === 0) {
        throw new Error('Spreadsheet is empty')
      }

      // Validate the data
      const validationResult = validateSpreadsheetData(response.values)

      if (
        validationResult.errors.length > 0 &&
        validationResult.data.length === 0
      ) {
        // Critical errors - no valid data
        setError(
          `Validation failed: ${validationResult.errors.map((e) => e.message).join('; ')}`,
        )
        setViewState('error')
        return
      }

      // Store data (even if there are some validation warnings)
      setSpreadsheetData(validationResult.data)
      setValidationErrors(validationResult.errors)
      setViewState('success')
    } catch (err: unknown) {
      // eslint-disable-next-line no-console
      console.error('Error loading spreadsheet:', err)

      // Handle specific error types
      const errorMessage = err instanceof Error ? err.message : String(err)
      if (
        errorMessage?.includes('401') ||
        errorMessage?.includes('unauthorized')
      ) {
        setError('Authentication expired. Please log in again.')
      } else if (
        errorMessage?.includes('403') ||
        errorMessage?.includes('permission')
      ) {
        setError(
          'Permission denied. Make sure the spreadsheet is shared with your account or is publicly accessible.',
        )
      } else if (
        errorMessage?.includes('404') ||
        errorMessage?.includes('not found')
      ) {
        setError('Spreadsheet not found. Check that the URL or ID is correct.')
      } else {
        setError(
          errorMessage || 'Failed to load spreadsheet. Please try again.',
        )
      }

      setViewState('error')
    }
  }

  const handleRetry = () => {
    setError(null)
    setViewState(isAuthenticated ? 'input' : 'instructions')
  }

  const handleStartReplacement = async () => {
    setReplacementLoading(true)
    setReplacementError(null)

    try {
      const newFlowId = await startUrlReplacement({
        tree_id: treeId,
        version_id: versionId,
        replacements: spreadsheetData,
      })
      setFlowId(newFlowId)
    } catch (err) {
      setReplacementError(err instanceof Error ? err.message : String(err))
    } finally {
      setReplacementLoading(false)
    }
  }

  const handleResetReplacement = () => {
    setFlowId(undefined)
    setReplacementError(null)
  }

  return (
    <Box>
      <Instructions />

      {/* Authentication Section */}
      {!isAuthenticated && (
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Step 1: Authenticate with Google Drive
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            You need to authorize access to your Google Drive to load
            spreadsheets.
          </Typography>
          <GoogleLoginButton />
        </Paper>
      )}

      {/* Input Section */}
      {isAuthenticated && viewState !== 'success' && (
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Step 2: Load Your Spreadsheet
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Paste the URL or ID of your Google Spreadsheet containing the URL
            mappings.
          </Typography>

          {error && viewState === 'error' && (
            <Box sx={{ mb: 2 }}>
              <ErrorDisplay error={error} onRetry={handleRetry} />
            </Box>
          )}

          <Box sx={{ mb: 3 }}>
            <SpreadsheetInput
              onLoad={handleLoadSpreadsheet}
              loading={viewState === 'loading'}
              disabled={viewState === 'loading'}
            />
          </Box>

          {viewState === 'loading' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary">
                Loading spreadsheet...
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {/* Success - Show Table and Start Replacement */}
      {viewState === 'success' && !flowId && (
        <Paper elevation={1} sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Step 3: Review URL Mappings
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Review your URL mappings below.{' '}
            {validationErrors.length > 0 &&
              'Note: Some rows have validation issues highlighted in orange.'}
          </Typography>

          <UrlMappingTable
            data={spreadsheetData}
            validationErrors={validationErrors}
          />

          {replacementError && (
            <Box sx={{ mt: 2 }}>
              <ErrorDisplay
                error={replacementError}
                onRetry={handleStartReplacement}
                title="Replacement Failed"
                retryLabel="Retry Replacement"
              />
            </Box>
          )}

          <Box sx={{ mt: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button
              variant="contained"
              onClick={handleStartReplacement}
              disabled={
                replacementLoading ||
                spreadsheetData.length === 0 ||
                (validationErrors.length > 0 && spreadsheetData.length === 0)
              }
              startIcon={
                replacementLoading ? <CircularProgress size={20} /> : null
              }
            >
              {replacementLoading
                ? 'Starting Replacement...'
                : 'Start URL Replacement'}
            </Button>
            {spreadsheetData.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                {spreadsheetData.length} URL mapping
                {spreadsheetData.length !== 1 ? 's' : ''} ready to replace
              </Typography>
            )}
          </Box>
        </Paper>
      )}

      {/* Show Job Status */}
      {flowId && (
        <Paper elevation={1} sx={{ p: 3 }}>
          <Box marginBottom="10px">
            <Button
              variant="outlined"
              size="small"
              onClick={handleResetReplacement}
            >
              Start New Replacement
            </Button>
          </Box>
          <FlowIdView<UrlReplacementJob> flowId={flowId} />
        </Paper>
      )}
    </Box>
  )
}
